import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const drawLine = keyframes`
  from { stroke-dashoffset: 1000; }
  to { stroke-dashoffset: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100%;
  height: 300px;
  background: rgba(20, 20, 30, 0.4);
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.6), transparent);
  }
`;

const ChartSVG = styled.svg`
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const CurvePath = styled.path`
  fill: none;
  stroke: #0aff0a;
  stroke-width: 3;
  stroke-linecap: round;
  filter: drop-shadow(0 0 8px rgba(10, 255, 10, 0.5));
  stroke-dasharray: 1000;
  stroke-dashoffset: 1000;
  animation: ${drawLine} 3s ease-out forwards;
`;

const AreaPath = styled.path`
  fill: url(#gradientArea);
  opacity: 0;
  animation: ${fadeIn} 1s ease-out 2s forwards;
`;

const Axis = styled.line`
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 1;
`;

const Label = styled.text`
  fill: rgba(255, 255, 255, 0.5);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
`;

const PricePoint = styled.circle`
  fill: #ffffff;
  filter: drop-shadow(0 0 10px #ffffff);
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out 2.5s forwards;
`;

export const PriceDiscoveryChart = () => {
    // Constant product curve points
    // y = k/x, but we want to show price increasing, so let's visualize Price vs Time or Price vs Volume
    // Let's do a nice exponential curve up and to the right

    const width = 500;
    const height = 250;
    const padding = 40;

    const generateCurve = () => {
        let d = `M ${padding} ${height - padding}`;
        for (let x = 0; x <= width - 2 * padding; x += 5) {
            // Exponential growth curve
            const normalizedX = x / (width - 2 * padding);
            const y = (height - padding) - (Math.pow(normalizedX, 2) * (height - 2 * padding));
            d += ` L ${x + padding} ${y}`;
        }
        return d;
    };

    const generateArea = () => {
        let d = generateCurve();
        d += ` L ${width - padding} ${height - padding} L ${padding} ${height - padding} Z`;
        return d;
    };

    return (
        <Container>
            <ChartSVG viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <defs>
                    <linearGradient id="gradientArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(10, 255, 10, 0.2)" />
                        <stop offset="100%" stopColor="rgba(10, 255, 10, 0)" />
                    </linearGradient>
                </defs>

                {/* Axes */}
                <Axis x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} />
                <Axis x1={padding} y1={height - padding} x2={padding} y2={padding} />

                {/* Labels */}
                <Label x={width - padding} y={height - 15} textAnchor="end">Volume</Label>
                <Label x={padding + 10} y={padding} textAnchor="start">Price</Label>

                {/* Curve */}
                <AreaPath d={generateArea()} />
                <CurvePath d={generateCurve()} />

                {/* Current Price Point */}
                <PricePoint cx={width - padding} cy={padding + 20} r="4" />

            </ChartSVG>
        </Container>
    );
};
